import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useGoogleLogin } from '@react-oauth/google';
import CourierLoginForm, { CredentialsResult, SendOtpResult, OtpVerifyResult, OtpMethod } from './CourierLoginForm';
import RoleSelector, { LoginRole } from './RoleSelector';
import SecurityBadge from './SecurityBadge';
import { cardVariants } from './animations';
import { Button } from '../../shared/components/ui';
import { API_BASE_URL, authService } from '../../services';

interface LoginCardProps {
  /** Callback when Google login succeeds */
  onGoogleLogin: (credential: string) => Promise<boolean>;
  /** Callback for courier credentials validation (Step 1) */
  onCourierCredentials: (username: string, idNumber: string) => Promise<CredentialsResult>;
  /** Callback for sending OTP via selected method (Step 2) */
  onCourierSendOtp: (username: string, idNumber: string, method: OtpMethod) => Promise<SendOtpResult>;
  /** Callback for OTP verification (Step 3) */
  onCourierVerifyOtp: (username: string, idNumber: string, code: string) => Promise<OtpVerifyResult>;
  /** Callback to resend OTP */
  onCourierResendOtp: (username: string, idNumber: string, method: OtpMethod) => Promise<SendOtpResult>;
}

/**
 * LoginCard Component
 * 
 * Main login card with role selection:
 * - HAAT Employee: Google SSO login
 * - Courier: Three-step authentication (credentials + method selection + OTP)
 * 
 * @example
 * <LoginCard 
 *   onGoogleLogin={handleGoogleLogin} 
 *   onCourierCredentials={handleCredentials}
 *   onCourierSendOtp={handleSendOtp}
 *   onCourierVerifyOtp={handleVerifyOtp}
 *   onCourierResendOtp={handleResendOtp}
 * />
 */
const LoginCard: React.FC<LoginCardProps> = ({
  onGoogleLogin: _onGoogleLogin,
  onCourierCredentials,
  onCourierSendOtp,
  onCourierVerifyOtp,
  onCourierResendOtp,
}) => {
  const [selectedRole, setSelectedRole] = useState<LoginRole>('employee');
  const [ssoError, setSsoError] = useState<string | null>(null);
  const [isLoadingSso, setIsLoadingSso] = useState(false);
  const { t } = useTranslation('auth');

  /**
   * Unified SSO login using Google Account Picker
   * 1. Shows Google account picker via OAuth
   * 2. Gets user's email from selected account
   * 3. Redirects to correct SAML endpoint based on email domain
   */
  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      // Keep loading true - we're processing and will redirect to SAML
      setSsoError(null);

      try {
        // Fetch user info to get email
        const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
        });

        if (!userInfoResponse.ok) {
          throw new Error('Failed to get user info');
        }

        const userInfo = await userInfoResponse.json();
        const email = userInfo.email;
        const domain = email.split('@')[1]?.toLowerCase();

        // Determine which SAML endpoint to use based on domain
        let samlEndpoint: string;
        if (domain === 'team.haat.delivery') {
          samlEndpoint = `${API_BASE_URL.replace(/\/$/, '')}/api/v1/auth/sso/login/team`;
        } else if (domain === 'haat.delivery') {
          samlEndpoint = `${API_BASE_URL.replace(/\/$/, '')}/api/v1/auth/sso/login/haat`;
        } else {
          try {
            await authService.logSsoDomainRejected(email);
          } catch (auditError) {
            console.warn('Failed to audit UI SSO domain rejection:', auditError);
          }
          setSsoError(t('login.employee.domainNotAllowed', 'Email domain not allowed. Please use your @team.haat or @haat email.'));
          setIsLoadingSso(false);
          return;
        }

        // Redirect to SAML with login_hint for seamless authentication
        window.location.href = `${samlEndpoint}?login_hint=${encodeURIComponent(email)}`;
      } catch (error) {
        console.error('SSO Error:', error);
        setSsoError(t('login.employee.ssoError', 'SSO login failed. Please try again.'));
        setIsLoadingSso(false);
      }
    },
    onError: (error) => {
      console.error('Google Login Error:', error);
      setSsoError(t('login.employee.ssoError', 'SSO login failed. Please try again.'));
      setIsLoadingSso(false);
    },
    // Handle popup closed/cancelled - resets loading state when user closes popup
    onNonOAuthError: (error) => {
      console.log('Google popup closed or cancelled:', error);
      // Don't show error message for user-initiated close, just reset loading
      setIsLoadingSso(false);
    },
  });

  const handleSsoClick = () => {
    setSsoError(null);
    setIsLoadingSso(true);
    googleLogin();
  };

  return (
    <motion.div
      variants={cardVariants}
      initial="initial"
      animate="animate"
      className="relative"
    >
      {/* Gradient Border Effect */}
      <div
        className="absolute -inset-[2px] bg-gradient-to-r from-haat-red to-haat-gold rounded-[22px] opacity-80"
        aria-hidden="true"
      />

      {/* Card Content */}
      <div className="relative bg-white rounded-[20px] p-8 shadow-xl">
        {/* Header */}
        <header className="text-center mb-6">
          <h2 className="text-xl font-semibold text-haat-dark mb-2 font-inter">
            {t('login.header.title')}
          </h2>
          <p className="text-sm text-haat-gray-light">
            {t('login.header.subtitle')}
          </p>
        </header>

        {/* Role Selector */}
        <RoleSelector
          selectedRole={selectedRole}
          onRoleChange={setSelectedRole}
        />

        {/* Login Forms */}
        <AnimatePresence mode="wait">
          {selectedRole === 'employee' ? (
            <motion.div
              key="employee"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2 }}
            >
              {ssoError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-[#c00035] text-sm">
                  {ssoError}
                </div>
              )}

              <Button
                type="button"
                onClick={handleSsoClick}
                variant="primary"
                size="lg"
                className="w-full"
                disabled={isLoadingSso}
              >
                {isLoadingSso ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {t('login.employee.signingIn', 'Signing in...')}
                  </span>
                ) : (
                  t('login.employee.ssoButton')
                )}
              </Button>
            </motion.div>
          ) : (
            <motion.div
              key="courier"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <CourierLoginForm
                onSubmitCredentials={onCourierCredentials}
                onSendOtp={onCourierSendOtp}
                onVerifyOtp={onCourierVerifyOtp}
                onResendOtp={onCourierResendOtp}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Security Badge */}
        <div className="mt-6">
          <SecurityBadge />
        </div>
      </div>
    </motion.div>
  );
};

export default LoginCard;
